import React, { useEffect, useState } from 'react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const cache = {};

/**
 * usePageContent(page) -> { c, t, ready, isPreview }
 *  - c: merged content object (published, or the admin's DRAFT when the URL has ?preview=draft and an admin token exists)
 *  - t(key, fallback): safe getter that falls back to the hard-coded copy while loading / if the API is unavailable
 * Public pages keep rendering their built-in text until the content arrives, so nothing flashes empty.
 */
export function usePageContent(page) {
  const params = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
  const wantPreview = params?.get('preview') === 'draft';
  const token = wantPreview ? localStorage.getItem('gs_admin_token') : null;
  const isPreview = !!(wantPreview && token);
  const [c, setC] = useState(() => (!isPreview && cache[page]) || null);

  useEffect(() => {
    let alive = true;
    const url = isPreview ? `${BACKEND}/api/admin/content/${page}/preview` : `${BACKEND}/api/content/${page}`;
    fetch(url, isPreview ? { headers: { Authorization: `Bearer ${token}` } } : undefined)
      .then(r => (r.ok ? r.json() : Promise.reject(new Error('content unavailable'))))
      .then(d => { if (!alive) return; if (!isPreview) cache[page] = d; setC(d); })
      .catch(() => { if (alive && !isPreview) setC(cache[page] || {}); else if (alive) fetch(`${BACKEND}/api/content/${page}`).then(r => r.json()).then(d => alive && setC(d)).catch(() => alive && setC({})); });
    return () => { alive = false; };
  }, [page, isPreview, token]);

  const t = (key, fallback = '') => (!c || c[key] === undefined || c[key] === null) ? fallback : c[key];
  return { c: c || {}, t, ready: !!c, isPreview };
}

/** Renders "paragraphs" content: blank-line separated, **bold** supported, everything else escaped by React. */
export function Paragraphs({ text, className = '' }) {
  const paras = String(text || '').split(/\n\s*\n/).map(p => p.trim()).filter(Boolean);
  return (
    <>
      {paras.map((p, i) => (
        <p key={i} className={className}>
          {p.split(/(\*\*[^*]+\*\*)/g).map((part, j) => part.startsWith('**') && part.endsWith('**')
            ? <strong key={j}>{part.slice(2, -2)}</strong>
            : <React.Fragment key={j}>{part}</React.Fragment>)}
        </p>
      ))}
    </>
  );
}

/** Small banner shown to an admin viewing a draft preview */
export function PreviewBanner({ page }) {
  return (
    <div className="sticky top-0 z-[60] bg-[hsl(var(--lux-gold))] text-[hsl(var(--lux-ink))] text-sm font-semibold text-center py-2 px-4 shadow" data-testid="cms-preview-banner">
      Draft preview — this is how "{page}" will look after you publish. Visitors still see the published version.
    </div>
  );
}
